import { useEffect, useState } from 'react';
import type { Pose } from '../types';
import { usePoseImageOverride } from '../data/poseImages';

interface Props {
  pose: Pose;
  showNumber?: boolean;
}

/**
 * Renders a pose's image. Prefers an admin-approved override (CloudFront), then
 * the bundled webp (public/poses/<id>.webp), then a placeholder — so a broken
 * override or a missing image both degrade cleanly.
 */
export function PoseMedia({ pose, showNumber = true }: Props) {
  const override = usePoseImageOverride(pose.id);
  const bundled = `${import.meta.env.BASE_URL}poses/${pose.id}.webp`;
  const [src, setSrc] = useState(override ?? bundled);
  const [failed, setFailed] = useState(false);

  // Re-resolve when the override manifest loads or changes.
  useEffect(() => {
    setSrc(override ?? bundled);
    setFailed(false);
  }, [override, bundled]);

  const handleError = () => {
    if (src !== bundled) setSrc(bundled); // override failed → try bundled
    else setFailed(true); // bundled failed → placeholder
  };

  // Draw the pose title as an overlay when the displayed art is "clean" (no
  // baked-in title): that's every override, plus bundled art flagged
  // overlayTitle (poses promoted from the runtime store). Older seed art has
  // the title baked in, so it opts out.
  const showTitle = !failed && (src !== bundled || pose.overlayTitle === true);

  return (
    <div className="media">
      {showNumber && <span className="num">#{pose.order}</span>}
      {showTitle && (
        <div className="media-title" aria-hidden="true">
          <span className="mt-name">{pose.nameEnglish}</span>
          {pose.nameSanskrit && <span className="mt-sanskrit">{pose.nameSanskrit}</span>}
        </div>
      )}
      {failed ? (
        <div className="media-fallback" role="img" aria-label={pose.media.alt}>
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="8.5" cy="9" r="1.6" />
            <path d="M21 16l-5-5L5 21" />
          </svg>
          <span className="label">media placeholder</span>
        </div>
      ) : (
        <img
          className="media-img"
          src={src}
          alt={pose.media.alt}
          loading="lazy"
          onError={handleError}
        />
      )}
    </div>
  );
}
